import React, { useState } from "react";
import { FormattedHTMLMessage, FormattedMessage } from "react-intl";
import { Grid, IconButton, Typography } from "@material-ui/core";
import ChangeAgentLevel from "./Dialogs/ChangeAgentLevel";
import ChangeCommissionLimit from "./Dialogs/ChangeCommissionLimit";
import ChangeJobTitle from "./Dialogs/ChangeJobTitle";
import ChangePermissions from "./Dialogs/ChangePermissions";
import ChangeManagerMonthlyCommission from "./Dialogs/ChangeManagementMonthlyCommission";
import ChangeSegment from "./Dialogs/ChangeSegment";
import ChangeVisibilityPermission from "./Dialogs/ChangeVisibilityPermission";
import EditIcon from "@material-ui/icons/Edit";
import useHasPermissions from "../../../hooks/useHasPermission";
import { AgreementLevelName, DIALOG_OPTION, Permissions } from "../../../util";
import NumberFormat from "../../../custom/Number";
import DisplayPermissionsDialog from "./Dialogs/DisplayPermissions";
import ChangeManagerOneTimeCommission from "./Dialogs/ChangeManagementOneTimeCommission";
import PermissionsInfo from "../PermissionInfo";

const AgreementConfig = ({ currentUser, user, fetchUser }) => {
  const [actionDialog, setActionDialog] = useState(null);
  const { hasPermission } = useHasPermissions();

  const hasRequiredLevel = (user, allowedLevels) => {
    return allowedLevels.includes(user.contractAgent?.contractAgentClass?.levelName);
  };

  const options = [
    {
      id: 1,
      text: "app.manager.userLevel",
      label: "contractAgent.contractAgentClass",
      onClick: () => setActionDialog(DIALOG_OPTION.CHANGE_AGENT_LEVEL),
      showOption: true,
      enableEdit: hasPermission([Permissions.EDIT_AGREEMENT_CLASS, Permissions.EDIT_AGREEMENT_CLASS_LOWER_LEVEL], user),
      type: "level",
    },
    {
      id: 2,
      text: "app.manager.jobTitle",
      label: "contractAgent.jobTitle",
      onClick: () => setActionDialog(DIALOG_OPTION.CHANGE_JOB_TITLE),
      showOption: true,
      enableEdit: hasPermission(Permissions.EDIT_AGREEMENT_JOB_TITLE),
      type: "text",
    },
    {
      id: 3,
      text: "app.manager.managerMonthlyCommission",
      label: "contractAgent.managerMonthlyCommission",
      onClick: () => setActionDialog(DIALOG_OPTION.CHANGE_MANAGER_MONTHLY_COMMISSION),
      showOption: hasRequiredLevel(user, [AgreementLevelName.AGENT_MANAGER]),
      enableEdit: hasPermission([Permissions.EDIT_AGREEMENT_MONTHLY_COMMISSION, Permissions.EDIT_AGREEMENT_MONTHLY_COMMISSION_LOWER_LEVEL], user),
      type: "percentage",
    },
    {
      id: 4,
      text: "app.manager.managerOneTimeCommission",
      label: "contractAgent.managerOneTimeCommission",
      onClick: () => setActionDialog(DIALOG_OPTION.CHANGE_MANAGER_ONE_TIME_COMMISSION),
      showOption:
        hasRequiredLevel(user, [AgreementLevelName.CCO, AgreementLevelName.SALES_DIRECTOR, AgreementLevelName.REGIONAL_MANAGER]) &&
        hasPermission([Permissions.EDIT_AGREEMENT_ONE_TIME_COMMISSION, Permissions.EDIT_AGREEMENT_ONE_TIME_COMMISSION_LOWER_LEVEL], user),
      enableEdit: hasPermission([Permissions.EDIT_AGREEMENT_ONE_TIME_COMMISSION, Permissions.EDIT_AGREEMENT_ONE_TIME_COMMISSION_LOWER_LEVEL], user),
      type: "percentage",
    },
    {
      id: 5,
      text: "app.manager.limitCommission",
      label: "contractAgent.commissionLimit",
      onClick: () => setActionDialog(DIALOG_OPTION.CHANGE_COMMISSION_LIMIT),
      showOption: true,
      enableEdit: hasPermission([Permissions.EDIT_AGREEMENT_LIMIT, Permissions.EDIT_AGREEMENT_LIMIT_LOWER_LEVEL],user),
      type: "percentage",
    },
    {
      id: 6,
      text: "app.manager.segment",
      label: "contractAgent.investment.description",
      onClick: () => setActionDialog(DIALOG_OPTION.CHANGE_SEGMENT_DEFAULT),
      showOption: hasPermission(Permissions.EDIT_AGREEMENT_INVESTMENT),
      enableEdit: hasPermission(Permissions.EDIT_AGREEMENT_INVESTMENT),
      type: "segment",
    },
    {
      id: 7,
      text: "app.manager.visibility",
      label: "contractAgent.visibilityPermission",
      onClick: () => setActionDialog(DIALOG_OPTION.CHANGE_VISIBILITY_PERMISSION),
      showOption:
        hasRequiredLevel(currentUser, [AgreementLevelName.AGENT_MANAGER, AgreementLevelName.REGIONAL_MANAGER, AgreementLevelName.SALES_DIRECTOR, AgreementLevelName.CCO]) &&
        hasPermission(Permissions.EDIT_AGREEMENT_VISIBILITY),
      enableEdit: hasPermission(Permissions.EDIT_AGREEMENT_VISIBILITY),
      type: "visibility",
    },
    {
      id: 8,
      text: "app.manager.permissions",
      label: "contractAgent.permissions",
      onClick: () => setActionDialog(DIALOG_OPTION.CHANGE_PERMISSIONS),
      showOption:
        hasRequiredLevel(currentUser, [AgreementLevelName.AGENT_MANAGER, AgreementLevelName.REGIONAL_MANAGER, AgreementLevelName.SALES_DIRECTOR, AgreementLevelName.CCO]) &&
        hasPermission(Permissions.EDIT_AGREEMENT_PERMISSIONS),
      enableEdit: hasPermission(Permissions.EDIT_AGREEMENT_PERMISSIONS),
      type: "permission",
    },
  ];

  const getLabelAndValue = ({ id, text, label, type, enableEdit, onClick }) => {
    let value = label.includes(".") ? label.split(".").reduce((acc, key) => acc && acc[key], user) : user[label];

    const getValueByType = () => {
      switch (type) {
        case "level":
          return (
            <Typography component={"span"}>
              <FormattedMessage id={`app.enuns.levelName.${value.levelName}`} />
            </Typography>
          );
        case "percentage":
          return (
            <NumberFormat
              value={value}
              display={"percentage"}
              hideValue={currentUser.hideValues}
            />
          );
        case "visibility":
          return <FormattedMessage id={`app.manager.visibility.${value}`} />;
        case "segment":
          return value;
        case "permission":
          return (
            <PermissionsInfo
              permissions={value}
              handleOpenDialog={() => setActionDialog(DIALOG_OPTION.DISPLAY_PERMISSIONS)}
            />
          );
        default:
          return value;
      }
    };
    return (
      <React.Fragment key={id}>
        <Typography
          color="textSecondary"
          variant="caption"
        >
          <FormattedMessage id={text} />
        </Typography>
        <Grid
          container
          alignContent="center"
        >
          <Typography
            paragraph
            variant="body1"
          >
            {getValueByType()}
            <span>
              {enableEdit && (
                <IconButton
                  style={{ marginBottom: 4 }}
                  size="small"
                  onClick={onClick}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </span>
          </Typography>
        </Grid>
      </React.Fragment>
    );
  };

  const handleCloseDialog = () => {
    setActionDialog(null);
  };

  return (
    <>
      <Typography align="center">
        <FormattedHTMLMessage id="app.contractAgent.info" />
      </Typography>

      {options.filter((d) => d.showOption).map((d) => getLabelAndValue(d))}

      {actionDialog === DIALOG_OPTION.CHANGE_AGENT_LEVEL && (
        <ChangeAgentLevel
          user={user}
          currentUser={currentUser}
          open={actionDialog === DIALOG_OPTION.CHANGE_AGENT_LEVEL}
          handleCloseDialog={handleCloseDialog}
          onComplete={() => {
            if (
              hasPermission([
                Permissions.EDIT_AGREEMENT_LIMIT,
                Permissions.EDIT_AGREEMENT_LIMIT_LOWER_LEVEL,
              ],user)
            )
             setActionDialog(DIALOG_OPTION.CHANGE_COMMISSION_LIMIT);
          }}
          fetchUser={fetchUser}
        />
      )}

      {actionDialog === DIALOG_OPTION.CHANGE_JOB_TITLE && (
        <ChangeJobTitle
          user={user}
          currentUser={currentUser}
          open={actionDialog === DIALOG_OPTION.CHANGE_JOB_TITLE}
          handleCloseDialog={handleCloseDialog}
          fetchUser={fetchUser}
        />
      )}

      {actionDialog === DIALOG_OPTION.CHANGE_MANAGER_MONTHLY_COMMISSION && (
        <ChangeManagerMonthlyCommission
          user={user}
          currentUser={currentUser}
          open={actionDialog === DIALOG_OPTION.CHANGE_MANAGER_MONTHLY_COMMISSION}
          handleCloseDialog={handleCloseDialog}
          fetchUser={fetchUser}
        />
      )}

      {actionDialog === DIALOG_OPTION.CHANGE_MANAGER_ONE_TIME_COMMISSION && (
        <ChangeManagerOneTimeCommission
          user={user}
          currentUser={currentUser}
          open={actionDialog === DIALOG_OPTION.CHANGE_MANAGER_ONE_TIME_COMMISSION}
          handleCloseDialog={handleCloseDialog}
          fetchUser={fetchUser}
        />
      )}

      {actionDialog === DIALOG_OPTION.CHANGE_COMMISSION_LIMIT && (
        <ChangeCommissionLimit
          user={user}
          currentUser={currentUser}
          open={actionDialog === DIALOG_OPTION.CHANGE_COMMISSION_LIMIT}
          handleCloseDialog={handleCloseDialog}
          fetchUser={fetchUser}
        />
      )}

      {actionDialog === DIALOG_OPTION.CHANGE_SEGMENT_DEFAULT && (
        <ChangeSegment
          user={user}
          currentUser={currentUser}
          open={actionDialog === DIALOG_OPTION.CHANGE_SEGMENT_DEFAULT}
          handleCloseDialog={handleCloseDialog}
          fetchUser={fetchUser}
        />
      )}

      {actionDialog === DIALOG_OPTION.CHANGE_VISIBILITY_PERMISSION && (
        <ChangeVisibilityPermission
          user={user}
          currentUser={currentUser}
          open={actionDialog === DIALOG_OPTION.CHANGE_VISIBILITY_PERMISSION}
          handleCloseDialog={handleCloseDialog}
          fetchUser={fetchUser}
        />
      )}

      {actionDialog === DIALOG_OPTION.CHANGE_PERMISSIONS && (
        <ChangePermissions
          user={user}
          currentUser={currentUser}
          open={actionDialog === DIALOG_OPTION.CHANGE_PERMISSIONS}
          handleCloseDialog={handleCloseDialog}
          fetchUser={fetchUser}
        />
      )}

      {actionDialog === DIALOG_OPTION.DISPLAY_PERMISSIONS && (
        <DisplayPermissionsDialog
          user={user}
          open={actionDialog === DIALOG_OPTION.DISPLAY_PERMISSIONS}
          handleCloseDialog={handleCloseDialog}
        />
      )}
    </>
  );
};

export default AgreementConfig;
